import React, { useState } from 'react';
import { ChartSetList } from '../../types';
import { useFeatureAccess } from '../../contexts/SubscriptionContext';

interface CreateSetlistFormProps {
  collectionsCount: number;
  onCreateCollection: (collection: Omit<ChartSetList, 'id' | 'createdAt' | 'lastModified'>) => void;
  onCancel: () => void;
}

const PREDEFINED_COLORS = [
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5A2B', // Brown
  '#6B7280', // Gray
  '#EC4899', // Pink
];

const CreateSetlistForm: React.FC<CreateSetlistFormProps> = ({
  collectionsCount,
  onCreateCollection,
  onCancel
}) => {
  const { canUse, upgrade } = useFeatureAccess();
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [newCollectionColor, setNewCollectionColor] = useState('#8B5CF6');

  const handleCreateCollection = () => {
    if (!newCollectionName.trim()) return;

    // Check free tier limits
    if (!canUse('unlimitedSetlists') && collectionsCount >= 1) {
      upgrade('unlimitedSetlists');
      return;
    }

    onCreateCollection({
      name: newCollectionName.trim(),
      description: newCollectionDescription.trim() || undefined,
      chartTitles: [],
      color: newCollectionColor
    });

    // Reset form
    setNewCollectionName('');
    setNewCollectionDescription('');
    setNewCollectionColor('#8B5CF6');
  };

  // Show upgrade prompt if at limit
  if (!canUse('unlimitedSetlists') && collectionsCount >= 1) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Create New Setlist</h3>
          <div className="text-sm text-gray-400">
            Free: {collectionsCount}/1 setlist, 5 songs max each
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg p-6 text-center">
          <h4 className="text-xl font-semibold text-white mb-3">Upgrade for Unlimited Setlists</h4>
          <p className="text-gray-300 mb-4">
            You've reached the free limit of 1 setlist. Upgrade to Premium to create unlimited setlists with unlimited songs.
          </p>
          <button
            onClick={() => upgrade('unlimitedSetlists')}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-semibold transition-all transform hover:scale-105"
          >
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">Create New Setlist</h3>
        {!canUse('unlimitedSetlists') && (
          <div className="text-sm text-gray-400">
            Free: {collectionsCount}/1 setlist, 5 songs max each
          </div>
        )}
      </div>
      
      <div className="space-y-4 max-w-lg">
        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-2">Setlist Name *</label>
          <input
            type="text"
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            placeholder="e.g., Wedding Reception, Jazz Night, Open Mic..."
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            maxLength={50}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-2">Description (Optional)</label>
          <textarea
            value={newCollectionDescription}
            onChange={(e) => setNewCollectionDescription(e.target.value)}
            placeholder="Brief description of this setlist..."
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            rows={3}
            maxLength={200}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-2">Color</label>
          <div className="flex gap-2">
            {PREDEFINED_COLORS.map(color => (
              <button
                key={color}
                onClick={() => setNewCollectionColor(color)}
                className={`w-8 h-8 rounded-full transition-all ${
                  newCollectionColor === color 
                    ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' 
                    : 'hover:scale-110'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={handleCreateCollection}
            disabled={!newCollectionName.trim()}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-semibold transition-colors"
          >
            Create Setlist
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md font-semibold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSetlistForm;
